import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectUserLoggedIn, RootState } from '../modules';

const TOUR_PERMANENT_SKIP_KEY = 'website_tour_permanently_skipped';

export const useTour = () => {
    const location = useLocation();
    const isLoggedIn = useSelector((state: RootState) => selectUserLoggedIn(state));
    const [isTourVisible, setIsTourVisible] = useState(false);
    const [showTourPrompt, setShowTourPrompt] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [tourSkippedThisSession, setTourSkippedThisSession] = useState(false);
    const [seenPathsThisSession, setSeenPathsThisSession] = useState<Set<string>>(new Set());
    const [tourSkippedGlobalSession, setTourSkippedGlobalSession] = useState(false); // Global session skip state

    useEffect(() => {
        // Don't show tour if user is not logged in
        if (!isLoggedIn) {
            console.log('❌ User not logged in - hiding tour');
            setShowTourPrompt(false);
            setIsTourVisible(false);
            return;
        }

        // Tour should ONLY show on /profile/assets initially, but allow continuation if tour is already running
        const isRestrictedProfilePath = location.pathname.startsWith('/profile/') && location.pathname !== '/profile/assets';
        const isExactProfile = location.pathname === '/profile';
        const isChartPage = location.pathname === '/chart' || location.pathname.startsWith('/chart');
        
        if (!isTourVisible && (isRestrictedProfilePath || isExactProfile || isChartPage)) {
            return;
        }

        // Close tour if user navigates to chart page while tour is running
        if (isTourVisible && isChartPage) {
            setIsTourVisible(false);
            setCurrentStep(0);
            return;
        }

        if (isTourVisible) {
            return;
        }

        // Delay để đảm bảo authentication state đã được set
        const timer = setTimeout(() => {
            const tourPermanentlySkipped = localStorage.getItem(TOUR_PERMANENT_SKIP_KEY);
            const isPermanentlySkipped = tourPermanentlySkipped === 'true';
            
            const currentPathSeen = seenPathsThisSession.has(location.pathname);
            
            const shouldShowPrompt = !isPermanentlySkipped && 
                                   !tourSkippedGlobalSession &&
                                   !tourSkippedThisSession && 
                                   !currentPathSeen;

            if (shouldShowPrompt) {
                setShowTourPrompt(true);
            }
        }, 1000);

        return () => clearTimeout(timer);
    }, [location.pathname, isLoggedIn, isTourVisible, tourSkippedGlobalSession, tourSkippedThisSession, seenPathsThisSession]);

    // Reset path-specific skip state when changing to a new tour-enabled path
    useEffect(() => {
        // Don't reset states if tour is currently running (to prevent interruption during auto-navigation)
        if (isTourVisible) {
            return;
        }
        
        // Only reset tourSkippedThisSession to allow tour on new paths
        // Keep tourSeenThisSession intact to prevent reappearing after completion
        setTourSkippedThisSession(false);

    }, [location.pathname, isTourVisible]);

    const startTour = () => {
        setShowTourPrompt(false);
        setIsTourVisible(true);
        setCurrentStep(0);
        setSeenPathsThisSession(prev => new Set(prev).add(location.pathname)); // Mark current path as seen
    };

    const skipTour = (rememberChoice: boolean) => {
        setShowTourPrompt(false);
        setSeenPathsThisSession(prev => new Set(prev).add(location.pathname)); // Mark current path as seen

        if (rememberChoice) {
            // Permanently skip tour
            localStorage.setItem(TOUR_PERMANENT_SKIP_KEY, 'true');
        } else {
            // Skip for current session only
            setTourSkippedGlobalSession(true);
        }
    };

    const completeTour = () => {
        console.log('🏁 completeTour called - closing tour');
        setIsTourVisible(false);
        setSeenPathsThisSession(prev => new Set(prev).add(location.pathname)); // Mark current path as seen
    };

    const resetTour = () => {
        localStorage.removeItem(TOUR_PERMANENT_SKIP_KEY);
        setTourSkippedGlobalSession(false);
        setTourSkippedThisSession(false);
        setSeenPathsThisSession(new Set());
        setShowTourPrompt(true);
    };

    return {
        isTourVisible,
        showTourPrompt,
        currentStep,
        setCurrentStep,
        startTour,
        skipTour,
        completeTour,
        resetTour,
        setIsTourVisible
    };
};
